<script lang="ts">
    import tabIcon from "@/assets/vardplatsoversikt.svg";
    import { TabColor } from "@/types";

    export default {
        name: "Patientoversikt",
        tabName: "Patientöversikt",
        patientBound: true,
        icon: tabIcon,
        refreshTab: true,
        tabColor: TabColor.IceBlue,
    };
</script>

<script setup lang="ts">
    import { computed, onMounted, onUnmounted, ref, toRefs } from "vue";
    import moment from "moment";
    import emitter from "tiny-emitter/instance";
    import { getConfig, getErrorMessage, klinikenApi } from "@/api";
    import { openDialog } from "@/utils";
    import store from "@/store";
    import KButton from "@/components/Button.vue";
    import { anmalanStatusIcons, preanestesiStatusIcons, kallelseStatusIcons } from "@/tabs/Operation/utilsTs";
    import OperationIcon from "@/assets/patientoversikt/operation.svg";
    import KallelserSmsIcon from "@/assets/patientoversikt/kallelser_sms.svg";
    import FilerIcon from "@/assets/patientoversikt/filer.svg";
    import PatientSummary from "@/components/PatientSummary.vue";
    import CardView from "@/components/CardView.vue";
    import Card from "@/components/Card.vue";
    import PatientoversiktCardListItem from "@/tabs/Patientoversikt/PatientoversiktCardListItem.vue";
    import { ModelName, PatientoversiktItem, PatientoversiktItemDisplay } from "@/tabs/Patientoversikt/types";
    import PatientRegistration from "@/tabs/Patient/PatientRegistration.vue";
    import Operationsinformation from "@/tabs/Operation/Operationsinformation.vue";
    import PreanestesibedomningVisa from "@/tabs/Operation/PreanestesibedomningVisa.vue";
    import Operationsjournal from "@/tabs/Operation/Operationsjournal.vue";
    import Operationsoversikt from "@/tabs/Operation/Operationsoversikt.vue";
    import Operationskoordinering from "@/tabs/Operation/Operationskoordinering.vue";
    import FilhanteringFiler from "@/tabs/Filhantering/FilhanteringFiler.vue";
    import ModalContainerMediaPreview from "@/components/generic/preview/media/ModalContainerMediaPreview.vue";

    const props = defineProps<{
        tabId: number;
    }>();

    const { tabId } = toRefs(props);

    defineExpose({
        tabId,
    });

    const operationItems = ref([]);
    const kallelserSmsItems = ref([]);
    const filerItems = ref([]);
    const fileId = ref<string | null>(null);
    const showFile = ref<boolean>(false);

    const patient = computed(() => {
        return store.getters["patientData/patient"];
    });

    const CATEGORY_LIMIT = 20;

    const mapToDisplayItem = (item: PatientoversiktItem): PatientoversiktItemDisplay => {
        let statusIcon = null;

        switch (item.model) {
            case ModelName.Anmalan:
                statusIcon = item.status ? anmalanStatusIcons[item.status as keyof typeof anmalanStatusIcons] : null;
                break;
            case ModelName.Preanestesibedomning:
                statusIcon = item.status
                    ? preanestesiStatusIcons[item.status as keyof typeof preanestesiStatusIcons]
                    : null;
                break;
            case ModelName.Operationsjournal:
                statusIcon = item.status ? anmalanStatusIcons[item.status as keyof typeof anmalanStatusIcons] : null;
                break;
            case ModelName.Kallelse:
                statusIcon = item.status ? kallelseStatusIcons[item.status as keyof typeof kallelseStatusIcons] : null;
                break;
            default:
                break;
        }

        return {
            ...item,
            created_at: moment(item.created_at).format("YYYY-MM-DD"),
            accountableName: item.accountableHealthcareProfessional?.user_display?.name,
            statusIcon,
        };
    };

    const fetchPatientoversikt = async () => {
        try {
            const { data } = await klinikenApi.get(
                `core/patientoversikt/${patient.value.id}/`,
                getConfig({ params: { category_limit: CATEGORY_LIMIT } })
            );

            const items = data.map(mapToDisplayItem);

            operationItems.value = items.filter((item: PatientoversiktItemDisplay) => item.category === "OPERATION");
            kallelserSmsItems.value = items.filter(
                (item: PatientoversiktItemDisplay) => item.category === "KALLELSER_SMS"
            );
            filerItems.value = items.filter((item: PatientoversiktItemDisplay) => item.category === "FILER");
        } catch (error) {
            console.error(error);
            openDialog("Kunde inte hämta patientöversikt. " + getErrorMessage(error), "error");
        }
    };

    const openItem = async (item: PatientoversiktItemDisplay) => {
        switch (item.model) {
            case ModelName.Anmalan:
                await store.dispatch("tabs/closeTab", Operationsinformation);
                store.dispatch("tabs/openTab", {
                    component: Operationsinformation,
                    parameters: { operationsanmalan_id: item.id },
                });
                break;
            case ModelName.Preanestesibedomning:
                await store.dispatch("tabs/closeTab", PreanestesibedomningVisa);
                store.dispatch("tabs/openTab", {
                    component: PreanestesibedomningVisa,
                    parameters: {
                        preanestesibedomningId: item.id,
                    },
                });
                break;
            case ModelName.Operationsjournal:
                await store.dispatch("tabs/closeTabWithUnsavedDataCheck", Operationsjournal);
                store.dispatch("tabs/openTab", {
                    component: Operationsjournal,
                    parameters: { operationsanmalan_id: item.extra?.anmalan },
                });
                break;
            case ModelName.Kallelse:
                fileId.value = item.extra?.fil ?? null;
                showFile.value = true;
                break;
            case ModelName.Fil:
                fileId.value = item.id;
                showFile.value = true;
                break;
            default:
                break;
        }
    };

    const closeFile = () => {
        showFile.value = false;
        fileId.value = null;
    };

    const openRedigeraPatient = async () => {
        await store.dispatch("tabs/closeTabWithUnsavedDataCheck", PatientRegistration);
        store.commit("patientData/setEditPatient", patient.value.id);
        await store.dispatch("tabs/openTab", {
            component: PatientRegistration,
        });
    };

    onMounted(async () => {
        await fetchPatientoversikt();

        emitter.on(`refreshTab-${tabId.value}`, fetchPatientoversikt);
    });

    onUnmounted(() => {
        emitter.off(`refreshTab-${tabId.value}`);
    });

    const openOperationer = async () => {
        await store.dispatch("tabs/closeTab", Operationsoversikt, { root: true });
        store.dispatch("tabs/openTab", {
            component: Operationsoversikt,
            props: {
                initialFilterParams: {
                    patient__personId: patient.value.personId,
                    show_strukna: true,
                },
                initialOverrideDefaultFilter: true,
            },
        });
    };
    const openKallelserSms = async () => {
        await store.dispatch("tabs/closeTab", Operationskoordinering, { root: true });
        store.dispatch("tabs/openTab", {
            component: Operationskoordinering,
            props: {
                initialFilterParams: {
                    patient__personId: patient.value.personId,
                    show_strukna: true,
                },
            },
        });
    };
    const openFiler = async () => {
        await store.dispatch("tabs/closeTab", FilhanteringFiler, { root: true });
        store.dispatch("tabs/openTab", {
            component: FilhanteringFiler,
        });
    };
</script>

<template>
    <div class="patientoversikt">
        <div class="header">
            <h2>Patientöversikt</h2>
            <KButton @click="openRedigeraPatient" variant="link-background-white">Patientuppgifter</KButton>
        </div>
        <PatientSummary :patient="patient" class="patient-summary" />
        <!-- TODO: Komponenter för framtida version -->
        <!-- <div class="placeholder-create-menu"></div> -->
        <div class="placeholder-switch-view"></div>
        <CardView>
            <Card
                header="Operation"
                :headerIcon="OperationIcon"
                footerLinkLabel="Alla operationer"
                @footerLinkCallback="openOperationer"
            >
                <PatientoversiktCardListItem v-for="item in operationItems" :item="item" @openItem="openItem" />
            </Card>
            <Card
                header="Kallelser & sms"
                :headerIcon="KallelserSmsIcon"
                footerLinkLabel="Alla kallelser & sms"
                @footerLinkCallback="openKallelserSms"
            >
                <PatientoversiktCardListItem v-for="item in kallelserSmsItems" :item="item" @openItem="openItem" />
            </Card>
            <Card header="Filer" :headerIcon="FilerIcon" footerLinkLabel="Alla filer" @footerLinkCallback="openFiler">
                <PatientoversiktCardListItem v-for="item in filerItems" :item="item" @openItem="openItem" />
            </Card>
        </CardView>
        <ModalContainerMediaPreview
            v-if="showFile"
            @close="closeFile"
            :showModal="showFile"
            :fileMeta="{
                patient: patient.id,
                id: fileId ?? '',
            }"
            :tabId="tabId"
        />
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";

    .patientoversikt {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .header {
            display: flex;
            gap: 20px;
        }

        .placeholder-create-menu {
            background-color: white;
            height: 110px;
            max-width: $form-width;
        }

        .placeholder-switch-view {
            height: 30px;
        }
    }
</style>
